import { ModifiersZTableModel, TTableOptions } from '@monorepo/ui';
import { TTableCellTree, TTableRowNode } from '../../../table.type';
import { TWipBreakDownTableWidgetData, TWipBreakDownTableWidgetMessage } from '@monorepo/types';
import { DEFAULT_ROW_CELL_FONT_SIZE_IN_VH, fillEmptyRows } from './centralized-table.model';

export const WIP_BREAK_DOWN_TABLE_MODEL = {
  createModel: (
    widgetData: TWipBreakDownTableWidgetData,
    data: TWipBreakDownTableWidgetMessage['series'],
    totalsRow?: TWipBreakDownTableWidgetMessage['series'][number],
  ) => {
    data = fillEmptyRows(data, widgetData.pagination.itemsPerPage);

    return ModifiersZTableModel.createTableModel(
      data,
      {
        headerConfig: createHeaderConfig(widgetData),
        ...(totalsRow
          ? {
              footerConfig: createFooterConfig(widgetData, totalsRow),
            }
          : {}),
        edit: {
          hasAddRow: false,
          hasDeleteRow: false,
        },
      },
      (item): TTableRowNode<TWipBreakDownTableWidgetMessage['series'][number]> => ({
        type: 'ROW',
        rowItem: item,
        cells: createRowCells(widgetData, item),
      }),
    );
  },

  createIdentityModel: (widgetData: TWipBreakDownTableWidgetData) => {
    return ModifiersZTableModel.createTableModel(
      [] as TWipBreakDownTableWidgetMessage['series'],
      {
        headerConfig: createHeaderConfig(widgetData),
      },
      (item) => ({
        type: 'ROW',
        rowItem: item,
        cells: [],
      }),
    );
  },
};

const createHeaderConfig = (widgetData: TWipBreakDownTableWidgetData): TTableOptions['headerConfig'] => {
  return widgetData.tableColumns.map((col): TTableOptions['headerConfig'][number] => ({
    type: 'TEXT_CELL' as const,
    text: col.name,
    align: col.font_align,
    fontSize: { value: DEFAULT_ROW_CELL_FONT_SIZE_IN_VH * widgetData.headerFontSizeMultiplier, unit: 'vh' },
    textOverflow: 'nowrap',
  }));
};

const createFooterConfig = (
  widgetData: TWipBreakDownTableWidgetData,
  data: TWipBreakDownTableWidgetMessage['series'][number],
): TTableOptions['footerConfig'] => {
  return widgetData.tableColumns.map((col) => ({
    type: 'TEXT_CELL' as const,
    text: getFooterCellText(col, data),
    ...(col.colored ? { textColor: 'YELLOW' } : {}),
    align: col.font_align,
    fontSize: { value: DEFAULT_ROW_CELL_FONT_SIZE_IN_VH * widgetData.footerFontSizeMultiplier, unit: 'vh' },
    textOverflow: 'nowrap',
  }));
};

const getFooterCellText = (
  column: TWipBreakDownTableWidgetData['tableColumns'][number],
  data: TWipBreakDownTableWidgetMessage['series'][number],
): string => {
  const str =
    (column.totals.showMaxWipLabel ? 'Max WIP:' : '') +
    (column.totals.showMinWipLabel ? 'Min WIP:' : '') +
    (column.totals.showTotalWipLabel ? 'Total WIP' : '');
  return `${str} ${data.find((item) => item.attribute === column.totals.totalProp)?.data || ''}`;
};

const createRowCells = (
  widgetData: TWipBreakDownTableWidgetData,
  item: TWipBreakDownTableWidgetMessage['series'][number],
): TTableCellTree[] => {
  return widgetData.tableColumns.map(
    (col, ind): TTableCellTree => ({
      type: 'TEXT_CELL' as const,
      text: formatCellValue(item, col),
      align: col.font_align,
      fontSize: { value: DEFAULT_ROW_CELL_FONT_SIZE_IN_VH * widgetData.rowsFontSizeMultiplier, unit: 'vh' },
      textOverflow: 'nowrap',
      ...(col.colored ? { textColor: 'YELLOW' } : {}),
      ...(col.showActiveStyleIcon && widgetData.activeStyleIndex === formatCellValue(item, col) && ind === 0
        ? {
            marker: {
              color: '',
              iconId: 'LOADING',
              customSize: {
                width: { value: 3, unit: 'vh' },
                height: { value: 3, unit: 'vh' },
              },
            },
          }
        : {}),
    }),
  );
};

const formatCellValue = (
  item: TWipBreakDownTableWidgetMessage['series'][number],
  column: TWipBreakDownTableWidgetData['tableColumns'][number],
): string => {
  const value = item.find((i) => i.attribute === column.prop)?.data;
  if (value === null || value === undefined) return '';
  return value.toString();
};
